import {
  BusinessOutlined,
  CalendarMonthOutlined,
  Check,
  ChevronRight,
  Close,
  GroupsOutlined,
  MoreVert,
  PublicOutlined,
  Refresh,
  Search,
  ThumbDownOutlined,
  ThumbUpOutlined,
  WarningAmberOutlined
} from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ConsolidatedTenantContactDataResponse,
  IExecutiveBriefing
} from "../../../interfaces/CloseTheLoop.interfaces";
import {
  useGetConsolidatedTenantContactsDataQuery,
  useGetCVCIQExecutiveOverviewQuery,
  useGetCVCIQProductSnapshotQuery,
  useGetExecutiveBriefingTenantProductsQuery,
  useGetExecutiveBriefingV1Query,
  useLazyGetExecutiveBriefingTenantProductsQuery
} from "../../../slices/CloseTheLoop.slice";
import { THEME } from "../utils/theme";
import ProductSnapshots from "./ProductSnapshots";

interface CustomerOption {
  tenantId: string;
  name: string;
  productCount: number;
}

const CUSTOMER_OPTION_BATCH_SIZE = 50;

interface ContactGroup {
  label: string;
  contacts: ConsolidatedTenantContactDataResponse[];
}

const surface = {
  border: `1px solid ${THEME.border}`,
  borderRadius: "7px",
  backgroundColor: "#FFFFFF",
  boxShadow: "0 2px 8px rgba(23, 43, 77, 0.06)"
};

const timeframeOrder = ["AllUp", "3 Months", "6 Months", "12 Months"];

const formatTimeframeLabel = (value: string) => (value === "AllUp" ? "All Up" : value);

type CurrentPostureSection = {
  heading: string;
  bullets: string[];
};

type CurrentPostureContent = {
  overview: string[];
  sections: CurrentPostureSection[];
};

const parseCurrentPosture = (value: string): CurrentPostureContent => {
  const content: CurrentPostureContent = { overview: [], sections: [] };
  const lines = value
    .replace(/\s*[•·]\s*/g, "\n• ")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  let currentSection: CurrentPostureSection | null = null;

  lines.forEach((line) => {
    const bulletMatch = line.match(/^[•·-]\s*(.+)$/);
    if (bulletMatch) {
      const bulletText = bulletMatch[1].trim();
      if (/^product overview\s*:/i.test(bulletText) && content.overview.length === 0 && content.sections.length === 0) {
        content.overview.push(bulletText);
        return;
      }
      if (!currentSection) {
        currentSection = { heading: "", bullets: [] };
        content.sections.push(currentSection);
      }
      currentSection.bullets.push(bulletText);
      return;
    }

    if (/^.+\s[—–-]\s\d+\s+feedback records?$/i.test(line)) {
      currentSection = { heading: line, bullets: [] };
      content.sections.push(currentSection);
      return;
    }

    if (currentSection?.bullets.length) {
      currentSection.bullets[currentSection.bullets.length - 1] += ` ${line}`;
      return;
    }

    content.overview.push(line);
  });

  return content;
};

const formatFeedbackDate = (value?: string) => {
  if (!value) return "Not available";

  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "Not available"
    : date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
};

const getFunctionValue = (data: IExecutiveBriefing | undefined, keys: string[]): unknown => {
  if (!data) return undefined;
  const property = Object.keys(data).find((key) =>
    keys.some((candidate) => candidate.toLowerCase() === key.toLowerCase())
  );
  return property ? data[property] : undefined;
};

const getFunctionText = (data: IExecutiveBriefing | undefined, keys: string[], fallback: string) => {
  const value = getFunctionValue(data, keys);
  return {
    value: value === undefined || value === null || value === "" ? fallback : String(value),
    live: value !== undefined && value !== null && value !== ""
  };
};

const SectionHeading: React.FC<{
  title: string;
  subtitle: string;
  tag?: "live" | "preview" | "mixed";
  tagPlacement?: "default" | "nextToTitle";
}> = ({ title, subtitle }) => (
  <Box sx={{ display: "flex", alignItems: "start", justifyContent: "space-between", gap: 2 }}>
    <Box>
      <Typography sx={{ fontSize: 18, fontWeight: 750, color: "#092442" }}>
        {title}{" "}
        {/* {tagPlacement === "nextToTitle" &&
          (tag === "live" ? <LiveTag /> : tag === "preview" ? <PreviewTag /> : tag === "mixed" ? <MixedTag /> : null)} */}
      </Typography>
      <Typography sx={{ mt: 0.2, fontSize: 14, color: THEME.textSecondary }}>{subtitle}</Typography>
    </Box>
    {/* {tagPlacement === "default" &&
      (tag === "live" ? <LiveTag /> : tag === "preview" ? <PreviewTag /> : tag === "mixed" ? <MixedTag /> : null)} */}
  </Box>
);

const contactGroupOrder = ["Account Management", "CSAM", "Security Specialists"];
const contactAvatarColors = [
  { background: "#DCEEFF", foreground: "#145A8D" },
  { background: "#E8E1FF", foreground: "#3924A8" },
  { background: "#D7F5EF", foreground: "#0B6257" },
  { background: "#FFE1C7", foreground: "#8A3700" },
  { background: "#FFDDE8", foreground: "#A4264E" }
];

const formatContactType = (contactType: string | null) => {
  const value = contactType?.trim();
  if (!value) return "Other contacts";

  const normalized = value.toLowerCase().replace(/[^a-z0-9]/g, "");
  if (normalized.includes("account")) return "Account Management";
  if (normalized.includes("csam")) return "CSAM";
  if (normalized.includes("security") || normalized.includes("specialist")) return "Security Specialists";

  return value.replace(/([a-z])([A-Z])/g, "$1 $2");
};

const getContactName = (contact: ConsolidatedTenantContactDataResponse) =>
  contact.fullName || contact.name || "Unknown contact";

const getContactInitials = (contact: ConsolidatedTenantContactDataResponse) =>
  getContactName(contact)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase() || "?";

const getContactBadge = (contact: ConsolidatedTenantContactDataResponse) => {
  const group = formatContactType(contact.contactType);
  if (group === "Account Management") return "Account Manager";
  if (group === "Security Specialists") return "Security Specialist";
  return contact.contactType?.trim() || group;
};

const getContactBadgeColors = (contact: ConsolidatedTenantContactDataResponse) => {
  const group = formatContactType(contact.contactType);
  if (group === "Account Management") return { background: "#E1EFFF", foreground: "#075ED1" };
  if (group === "Security Specialists") return { background: "#FFE1EA", foreground: "#C50F1F" };
  return { background: "#EAE3FF", foreground: "#4F20D0" };
};

const groupContacts = (contacts: ConsolidatedTenantContactDataResponse[]): ContactGroup[] => {
  const groups = contacts.reduce<Map<string, ConsolidatedTenantContactDataResponse[]>>((result, contact) => {
    const label = formatContactType(contact.contactType);
    const group = result.get(label);
    if (group) {
      group.push(contact);
    } else {
      result.set(label, [contact]);
    }
    return result;
  }, new Map());

  return Array.from(groups, ([label, group]) => ({
    label,
    contacts: [...group].sort((left, right) => getContactName(left).localeCompare(getContactName(right)))
  })).sort((left, right) => {
    const leftIndex = contactGroupOrder.indexOf(left.label);
    const rightIndex = contactGroupOrder.indexOf(right.label);
    if (leftIndex === -1 && rightIndex === -1) return left.label.localeCompare(right.label);
    if (leftIndex === -1) return 1;
    if (rightIndex === -1) return -1;
    return leftIndex - rightIndex;
  });
};

const ContactRow: React.FC<{
  contact: ConsolidatedTenantContactDataResponse;
  colorIndex: number;
  showActions?: boolean;
}> = ({ contact, colorIndex, showActions = false }) => {
  const name = getContactName(contact);
  const avatarColors = contactAvatarColors[colorIndex % contactAvatarColors.length];
  const badgeColors = getContactBadgeColors(contact);

  return (
    <Box
      sx={{
        minHeight: 55,
        px: 1,
        py: 0.8,
        display: "grid",
        gridTemplateColumns: showActions ? "38px minmax(0, 1fr) auto 24px" : "38px minmax(0, 1fr) auto",
        alignItems: "center",
        gap: 1,
        borderTop: `1px solid ${THEME.border}`,
        bgcolor: "#FFFFFF"
      }}
    >
      <Box
        sx={{
          width: 34,
          height: 34,
          display: "grid",
          placeItems: "center",
          borderRadius: "50%",
          bgcolor: avatarColors.background,
          color: avatarColors.foreground,
          fontSize: 14,
          fontWeight: 800
        }}
      >
        {getContactInitials(contact)}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography noWrap title={name} sx={{ fontSize: 14, fontWeight: 800, color: "#092442" }}>
          {name}
        </Typography>
        <Typography noWrap sx={{ fontSize: 12, color: THEME.textSecondary }}>
          {contact.title || contact.nameRole || "Role not available"}
        </Typography>
        {contact.emailAddress && (
          <Typography noWrap title={contact.emailAddress} sx={{ fontSize: 12, color: "#49627B" }}>
            {contact.emailAddress}
          </Typography>
        )}
      </Box>
      <Chip
        label={getContactBadge(contact)}
        size="small"
        sx={{
          height: 24,
          maxWidth: 140,
          borderRadius: "6px",
          bgcolor: badgeColors.background,
          color: badgeColors.foreground,
          fontSize: 12,
          fontWeight: 800,
          "& .MuiChip-label": { px: 1, overflow: "hidden", textOverflow: "ellipsis" }
        }}
      />
      {showActions && (
        <IconButton
          component={contact.emailAddress ? "a" : "button"}
          href={contact.emailAddress ? `mailto:${contact.emailAddress}` : undefined}
          disabled={!contact.emailAddress}
          aria-label={contact.emailAddress ? `Email ${name}` : `No email available for ${name}`}
          size="small"
          sx={{ color: "#174E7A" }}
        >
          <MoreVert sx={{ fontSize: 16 }} />
        </IconButton>
      )}
    </Box>
  );
};
const ExecutiveBriefing: React.FC = () => {
  const customerAutocompleteRef = useRef<HTMLDivElement>(null);
  const customerSearchRef = useRef("");
  const [customerAutocompleteOpen, setCustomerAutocompleteOpen] = useState(false);
  const [customerSearch, setCustomerSearch] = useState("");
  const [debouncedCustomerSearch, setDebouncedCustomerSearch] = useState("");
  const [additionalCustomerOptions, setAdditionalCustomerOptions] = useState<CustomerOption[]>([]);
  const [customerNextCursor, setCustomerNextCursor] = useState<string | null>(null);
  const [isCustomerPaginationError, setIsCustomerPaginationError] = useState(false);
  const [helpful, setHelpful] = useState<boolean | null>(null);
  const [selectedTenantId, setSelectedTenantId] = useState("");
  const effectiveTenantId = selectedTenantId;
  const {
    data: briefings,
    isLoading,
    isError,
    refetch,
    isFetching
  } = useGetExecutiveBriefingV1Query(effectiveTenantId, { skip: !effectiveTenantId });
  const [selectedProduct, setSelectedProduct] = useState("AllUp");
  const [timeframe, setTimeframe] = useState("3 Months");
  const skipExecutiveOverview = !effectiveTenantId || !selectedProduct || !timeframe;
  const executiveOverviewQuery = useGetCVCIQExecutiveOverviewQuery(
    { tenantId: effectiveTenantId, productName: selectedProduct, feedbackPeriod: timeframe },
    { skip: skipExecutiveOverview }
  );
  const productSnapshotQuery = useGetCVCIQProductSnapshotQuery(
    { tenantId: effectiveTenantId, productName: selectedProduct, feedbackPeriod: timeframe },
    { skip: skipExecutiveOverview }
  );
  const [contactsDialogOpen, setContactsDialogOpen] = useState(false);
  const [contactSearch, setContactSearch] = useState("");
  const [contactRoleFilter, setContactRoleFilter] = useState("All roles");
  const [contactSort, setContactSort] = useState<"asc" | "desc">("asc");
  const {
    currentData: tenantProductsPage,
    isLoading: isLoadingTenantProducts,
    isFetching: isFetchingTenantProducts,
    isError: isErrorTenantProducts
  } = useGetExecutiveBriefingTenantProductsQuery({
    search: debouncedCustomerSearch || undefined,
    take: CUSTOMER_OPTION_BATCH_SIZE
  });
  const [loadMoreTenantProducts, { isFetching: isFetchingMoreTenantProducts }] =
    useLazyGetExecutiveBriefingTenantProductsQuery();

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      const normalizedSearch = customerSearch.trim();
      customerSearchRef.current = normalizedSearch;
      setDebouncedCustomerSearch(normalizedSearch);
    }, 300);
    return () => window.clearTimeout(timeoutId);
  }, [customerSearch]);

  useEffect(() => {
    setAdditionalCustomerOptions([]);
    setCustomerNextCursor(tenantProductsPage?.nextCursor ?? null);
    setIsCustomerPaginationError(false);
  }, [debouncedCustomerSearch, tenantProductsPage]);

  const loadMoreCustomerOptions = useCallback(
    async (cursor: string) => {
      const requestedSearch = debouncedCustomerSearch;
      setIsCustomerPaginationError(false);
      try {
        const page = await loadMoreTenantProducts({
          search: requestedSearch || undefined,
          cursor,
          take: CUSTOMER_OPTION_BATCH_SIZE
        }).unwrap();

        if (requestedSearch !== customerSearchRef.current) return;

        setAdditionalCustomerOptions((currentOptions) => {
          const existingTenantIds = new Set(currentOptions.map(({ tenantId }) => tenantId));
          const nextOptions = page.items
            .filter(({ tenantId }) => !existingTenantIds.has(tenantId))
            .map(({ tenantId, tenantName, products }) => ({
              tenantId,
              name: tenantName,
              productCount: products?.length ?? 0
            }));
          return [...currentOptions, ...nextOptions];
        });
        setCustomerNextCursor(page.nextCursor);
      } catch {
        setIsCustomerPaginationError(true);
      }
    },
    [debouncedCustomerSearch, loadMoreTenantProducts]
  );

  useEffect(() => {
    if (!customerAutocompleteOpen) return;

    const scrollTargets: EventTarget[] = [];
    let parent = customerAutocompleteRef.current?.parentElement;
    while (parent) {
      scrollTargets.push(parent);
      parent = parent.parentElement;
    }
    scrollTargets.push(window);

    const handleScroll = () => setCustomerAutocompleteOpen(false);
    scrollTargets.forEach((target) => target.addEventListener("scroll", handleScroll, { passive: true }));

    return () => {
      scrollTargets.forEach((target) => target.removeEventListener("scroll", handleScroll));
    };
  }, [customerAutocompleteOpen]);
  const {
    data: consolidatedTenantContacts,
    isLoading: isLoadingContacts,
    isError: isErrorContacts,
    refetch: refetchContacts,
    isFetching: isFetchingContacts
  } = useGetConsolidatedTenantContactsDataQuery(effectiveTenantId, { skip: !effectiveTenantId });
  const contactGroups = useMemo(() => groupContacts(consolidatedTenantContacts ?? []), [consolidatedTenantContacts]);
  const contactRoleOptions = useMemo(() => contactGroups.map(({ label }) => label), [contactGroups]);
  const filteredContactGroups = useMemo(() => {
    const query = contactSearch.trim().toLowerCase();
    const filteredContacts = (consolidatedTenantContacts ?? []).filter((contact) => {
      const matchesRole =
        contactRoleFilter === "All roles" || formatContactType(contact.contactType) === contactRoleFilter;
      const matchesSearch =
        !query ||
        [getContactName(contact), contact.title, contact.nameRole, contact.emailAddress, contact.contactType].some(
          (value) => value?.toLowerCase().includes(query)
        );
      return matchesRole && matchesSearch;
    });

    return groupContacts(filteredContacts).map((group) => ({
      ...group,
      contacts: contactSort === "asc" ? group.contacts : [...group.contacts].reverse()
    }));
  }, [consolidatedTenantContacts, contactRoleFilter, contactSearch, contactSort]);
  const filteredContactCount = filteredContactGroups.reduce((total, group) => total + group.contacts.length, 0);
  const customerOptions: CustomerOption[] = useMemo(() => {
    const firstPageOptions = (tenantProductsPage?.items ?? []).map(({ tenantId, tenantName, products }) => ({
      tenantId,
      name: tenantName,
      productCount: products?.length ?? 0
    }));
    const optionsByTenant = new Map(
      [...firstPageOptions, ...additionalCustomerOptions].map((option) => [option.tenantId, option])
    );
    return Array.from(optionsByTenant.values());
  }, [additionalCustomerOptions, tenantProductsPage]);
  const selectedCustomer =
    customerOptions.find((option) => option.tenantId === effectiveTenantId) ??
    (effectiveTenantId
      ? {
          tenantId: effectiveTenantId,
          name:
            (briefings ?? []).find(({ tenantId }) => tenantId === effectiveTenantId)?.tenantName ?? effectiveTenantId,
          productCount: new Set(
            (briefings ?? [])
              .filter(({ tenantId }) => tenantId === effectiveTenantId)
              .map(({ productName }) => productName)
          ).size
        }
      : undefined);
  const customerBriefings = useMemo(
    () => (briefings ?? []).filter((briefing) => briefing.tenantId === effectiveTenantId),
    [briefings, effectiveTenantId]
  );
  const selectedTenantProducts = useMemo(
    () => tenantProductsPage?.items.find(({ tenantId }) => tenantId === effectiveTenantId),
    [effectiveTenantId, tenantProductsPage]
  );
  const productOptions = useMemo(
    () =>
      Array.from(
        new Set(
          (selectedTenantProducts?.products ?? [])
            .map((productName) => productName.trim())
            .filter((productName): productName is string => Boolean(productName))
        )
      ).sort((left, right) => left.localeCompare(right, undefined, { numeric: true, sensitivity: "base" })),
    [selectedTenantProducts]
  );
  const timeframeOptions = useMemo(() => {
    const tenantFeedbackPeriods = selectedTenantProducts?.feedbackPeriods ?? [];
    const productFeedbackPeriods = selectedProduct
      ? tenantFeedbackPeriods.filter(({ productName }) => productName === selectedProduct)
      : tenantFeedbackPeriods;

    return timeframeOrder.map((value) => {
      const feedbackPeriod = productFeedbackPeriods.find(
        ({ feedbackPeriod }) => feedbackPeriod?.toLowerCase() === value.toLowerCase()
      );
      return {
        value,
        label: formatTimeframeLabel(value),
        hasDateRange: Boolean(feedbackPeriod?.feedbackStartDate && feedbackPeriod?.feedbackEndDate),
        startDate: formatFeedbackDate(feedbackPeriod?.feedbackStartDate ?? undefined),
        endDate: formatFeedbackDate(feedbackPeriod?.feedbackEndDate ?? undefined)
      };
    });
  }, [selectedProduct, selectedTenantProducts]);
  useEffect(() => {
    if (!effectiveTenantId || timeframeOptions.some((option) => option.value === timeframe && option.hasDateRange)) {
      return;
    }

    const firstAvailableTimeframe = timeframeOptions.find(({ hasDateRange }) => hasDateRange);
    if (firstAvailableTimeframe) {
      setTimeframe(firstAvailableTimeframe.value);
    }
  }, [effectiveTenantId, timeframe, timeframeOptions]);
  const selectedBriefings = useMemo(
    () =>
      customerBriefings
        .filter(({ feedbackThemeType }) => feedbackThemeType?.trim() !== "AllUp")
        .filter(
          ({ productName, feedbackPeriod }) =>
            (!selectedProduct || productName === selectedProduct) &&
            (!timeframe || feedbackPeriod?.toLowerCase() === timeframe.toLowerCase())
        ),
    [customerBriefings, selectedProduct, timeframe]
  );
  const data = selectedBriefings[0] ?? customerBriefings[0];
  const currentPostureAISummary = executiveOverviewQuery.currentData?.unifiedAISummary?.trim() ?? "";
  const currentPostureContent = useMemo(() => parseCurrentPosture(currentPostureAISummary), [currentPostureAISummary]);
  const tenantLabel = data?.tenantId ? `Tenant ${data.tenantId.slice(0, 8)}…` : "Contoso Financial Services";
  const customer = getFunctionText(data, ["tenantName"], tenantLabel);
  const industry = customerBriefings.find(({ industryName }) => industryName?.trim())?.industryName || "";
  const region = customerBriefings.find(({ regionName }) => regionName?.trim())?.regionName || "";
  const segment = customerBriefings.find(({ segmentName }) => segmentName?.trim())?.segmentName || "";
  const customerName = customer.value;

  const resetDependentFilters = () => {
    setSelectedProduct("AllUp");
    setTimeframe("3 Months");
  };

  const handleCustomerChange = (tenantId: string) => {
    setSelectedTenantId(tenantId);
    resetDependentFilters();
  };

  return (
    <Box sx={{ width: "100%", color: "#092442", fontFamily: "'Segoe UI', sans-serif", pb: 3 }}>
      <Paper
        elevation={0}
        sx={{
          ...surface,
          borderRadius: 0,
          px: 1.5,
          py: 1.2,
          mb: 2,
          bgcolor: "#F7FAFC"
        }}
      >
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: { xs: "100%", sm: 460 },
              minWidth: { sm: 400 },
              p: 1.1,
              bgcolor: "#FFF",
              border: "1px solid #B8D5EA",
              borderRadius: "6px",
              boxShadow: "0 3px 10px rgba(9, 36, 66, 0.08)"
            }}
          >
            <Box
              sx={{
                mb: 0.7,
                display: "grid",
                gridTemplateColumns: "25px minmax(0, 1fr) auto",
                columnGap: 0.7,
                rowGap: 0.25,
                alignItems: "center"
              }}
            >
              <Box
                sx={{
                  width: 25,
                  height: 25,
                  display: "grid",
                  placeItems: "center",
                  borderRadius: "5px",
                  bgcolor: "#DCEEFF",
                  color: "#145A8D"
                }}
              >
                <BusinessOutlined sx={{ fontSize: 16 }} />
              </Box>
              <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#092442" }}>CUSTOMER PORTFOLIO</Typography>
              <Chip
                label={`${tenantProductsPage?.totalCount ?? 0} Total Customers`}
                size="small"
                sx={{
                  height: 24,
                  borderRadius: "4px",
                  bgcolor: "#EAF2F8",
                  color: "#174E7A",
                  fontSize: 12,
                  fontWeight: 800
                }}
              />
              <Typography sx={{ gridColumn: "2 / -1", fontSize: 12, color: THEME.textSecondary }}>
                Choose an account to refresh the briefing
              </Typography>
            </Box>
            {!isErrorTenantProducts ? (
              <Autocomplete
                ref={customerAutocompleteRef}
                size="small"
                autoHighlight
                openOnFocus
                disablePortal
                open={customerAutocompleteOpen}
                onOpen={() => setCustomerAutocompleteOpen(true)}
                onClose={() => setCustomerAutocompleteOpen(false)}
                onInputChange={(_event, value, reason) => {
                  if (reason === "input" || reason === "clear") {
                    customerSearchRef.current = value.trim();
                    setCustomerSearch(value);
                  }
                }}
                options={customerOptions}
                filterOptions={(options) => options}
                value={selectedCustomer ?? null}
                onChange={(_event, option) => handleCustomerChange(option?.tenantId ?? "")}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.tenantId === value.tenantId}
                componentsProps={{
                  popper: {
                    placement: "bottom-start",
                    modifiers: [
                      { name: "flip", enabled: false },
                      { name: "preventOverflow", options: { mainAxis: false, altAxis: true } }
                    ],
                    sx: { zIndex: 1 }
                  }
                }}
                ListboxProps={{
                  onScroll: (event) => {
                    const listbox = event.currentTarget;
                    const isNearBottom =
                      listbox.scrollTop + listbox.clientHeight >= listbox.scrollHeight - listbox.clientHeight / 2;
                    if (isNearBottom && customerNextCursor && !isFetchingMoreTenantProducts) {
                      void loadMoreCustomerOptions(customerNextCursor);
                    }
                  },
                  sx: { maxHeight: 320, p: 0.5 }
                }}
                loading={isLoadingTenantProducts || isFetchingTenantProducts}
                loadingText="Loading customers..."
                noOptionsText="No matching customers"
                renderOption={(props, option) => (
                  <Box
                    component="li"
                    {...props}
                    key={option.tenantId}
                    sx={{
                      display: "flex !important",
                      alignItems: "center",
                      gap: 1,
                      px: "9px !important",
                      py: "8px !important",
                      borderRadius: "4px",
                      borderBottom: "1px solid #EDF1F5"
                    }}
                  >
                    <Box
                      sx={{
                        flex: "0 0 auto",
                        width: 30,
                        height: 30,
                        display: "grid",
                        placeItems: "center",
                        borderRadius: "50%",
                        bgcolor: "#DCEEFF",
                        color: "#145A8D",
                        fontSize: 13,
                        fontWeight: 800
                      }}
                    >
                      {option.name.slice(0, 2).toUpperCase()}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography noWrap sx={{ fontSize: 14, fontWeight: 750, color: "#092442" }}>
                        {option.name}
                      </Typography>
                      <Typography noWrap sx={{ mt: 0.2, fontSize: 12, color: THEME.textSecondary }}>
                        {option.tenantId}
                      </Typography>
                    </Box>
                    <Chip
                      label={`${option.productCount} product${option.productCount === 1 ? "" : "s"}`}
                      size="small"
                      sx={{
                        height: 24,
                        borderRadius: "4px",
                        bgcolor: "#F3F9F2",
                        color: "#0B6A0B",
                        fontSize: 12,
                        fontWeight: 750
                      }}
                    />
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search and select a customer"
                    inputProps={{ ...params.inputProps, "aria-label": "Search and select customer" }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search sx={{ fontSize: 17, color: THEME.textSecondary }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <>
                          {(isFetchingTenantProducts || isFetchingMoreTenantProducts) && (
                            <CircularProgress color="inherit" size={14} />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }}
                  />
                )}
                sx={{
                  bgcolor: "#FFF",
                  "& .MuiOutlinedInput-root": {
                    py: 0.15,
                    pr: "34px !important",
                    fontSize: 14,
                    fontWeight: 750,
                    bgcolor: "#F9FCFF",
                    "& fieldset": { borderColor: "#8AB8D8" },
                    "&:hover fieldset": { borderColor: "#145A8D" },
                    "&.Mui-focused fieldset": { borderWidth: 2, borderColor: THEME.primary }
                  },
                  "& .MuiAutocomplete-input": { minWidth: "80px !important" }
                }}
              />
            ) : (
              <Box
                sx={{
                  mt: 0.4,
                  px: 1,
                  py: 0.7,
                  border: `1px solid ${THEME.border}`,
                  borderRadius: "3px",
                  fontSize: 14,
                  fontWeight: 700
                }}
              >
                Customer portfolio data could not be loaded
              </Box>
            )}
            {isCustomerPaginationError && (
              <Typography sx={{ mt: 0.5, fontSize: 12, color: "#A4262C" }}>
                More customers could not be loaded. Scroll again to retry.
              </Typography>
            )}
          </Box>
          <FormControl
            size="small"
            disabled={!effectiveTenantId}
            sx={{ minWidth: 210, p: 0.75, pt: 2, bgcolor: "#EEF6FC", border: "1px solid #9FC4E2", borderRadius: 1 }}
          >
            <InputLabel shrink sx={{ ml: 0.75, mt: 0.75, top: 10 }}>
              Product view
            </InputLabel>
            <Select
              value={selectedProduct}
              label="Product view"
              renderValue={(value) => (value === "AllUp" ? "All Up" : value)}
              onChange={(event) => {
                setSelectedProduct(event.target.value);
              }}
              sx={{ bgcolor: "#FFF", fontWeight: 700 }}
            >
              {productOptions.map((product) => (
                <MenuItem key={product} value={product}>
                  {product === "AllUp" ? "All Up" : product}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl
            size="small"
            disabled={!effectiveTenantId}
            sx={{ minWidth: 150, p: 0.75, pt: 2, bgcolor: "#EEF6FC", border: "1px solid #9FC4E2", borderRadius: 1 }}
          >
            <InputLabel sx={{ ml: 0.75, mt: 0.75, top: 10 }}>Timeframe</InputLabel>
            <Select
              value={timeframe}
              label="Timeframe"
              onChange={(event) => {
                setTimeframe(event.target.value);
              }}
              renderValue={(value) => formatTimeframeLabel(value)}
              sx={{ bgcolor: "#FFF" }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    mt: 0.5,
                    width: 380,
                    maxWidth: "calc(100vw - 32px)",
                    borderRadius: 1.5,
                    boxShadow: "0 8px 24px rgba(15, 45, 75, 0.18)"
                  }
                },
                MenuListProps: { sx: { p: 0 } }
              }}
            >
              {timeframeOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  disabled={!option.hasDateRange}
                  sx={{
                    minHeight: 76,
                    px: 2,
                    py: 1.3,
                    gap: 1.5,
                    borderBottom: `1px solid ${THEME.border}`,
                    whiteSpace: "normal",
                    "&.Mui-selected": { bgcolor: "#EEF6FF", "&:hover": { bgcolor: "#E7F1FC" } },
                    "&.Mui-disabled": { opacity: 1 }
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      flex: "0 0 auto",
                      display: "grid",
                      placeItems: "center",
                      borderRadius: 1,
                      bgcolor: option.hasDateRange ? "#EDF4FD" : "#F3F5F7",
                      color: option.hasDateRange ? THEME.primary : "#8A9BAB"
                    }}
                  >
                    <CalendarMonthOutlined />
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: "inherit", color: option.hasDateRange ? "#092442" : "#66778A" }}>
                      {option.label}
                    </Typography>
                    {option.hasDateRange ? (
                      <Typography sx={{ mt: 0.35, color: THEME.textSecondary, fontSize: 14 }}>
                        Start: {option.startDate}&nbsp;&nbsp;•&nbsp;&nbsp;End: {option.endDate}
                      </Typography>
                    ) : (
                      <Box sx={{ mt: 0.35, display: "flex", alignItems: "center", gap: 0.6, color: "#B35C00" }}>
                        <WarningAmberOutlined sx={{ fontSize: 16 }} />
                        <Typography sx={{ color: "inherit", fontSize: 14 }}>Date range unavailable</Typography>
                      </Box>
                    )}
                  </Box>
                  {option.hasDateRange && timeframe === option.value && <Check sx={{ color: THEME.primary }} />}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, flex: 1 }}>
            {/* <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: !effectiveTenantId ? "#8A9BAB" : data ? "#107C10" : "#D13438"
              }}
            />
            <Typography sx={{ fontSize: 10, fontWeight: 700 }}>
              {!effectiveTenantId
                ? "Select a customer to load a live snapshot"
                : data
                ? `Live snapshot · Updated ${generatedAt?.toLocaleString()}`
                : "Live snapshot unavailable"}
            </Typography> */}
          </Box>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            disabled={
              !effectiveTenantId ||
              isFetching ||
              executiveOverviewQuery.isFetching ||
              productSnapshotQuery.isFetching
            }
            onClick={() => {
              refetch();
              if (!skipExecutiveOverview) executiveOverviewQuery.refetch();
              if (!skipExecutiveOverview) productSnapshotQuery.refetch();
            }}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Refresh snapshot
          </Button>
          {/* <Button
            variant="contained"
            disabled={!effectiveTenantId}
            onClick={() => window.print()}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Export briefing
          </Button> */}
        </Box>
      </Paper>

      {isLoading ? (
        <Box
          component="main"
          aria-live="polite"
          sx={{
            minHeight: 500,
            mx: { xs: 0, md: 0.5 },
            display: "grid",
            placeItems: "center"
          }}
        >
          <Stack spacing={1.2} sx={{ alignItems: "center" }}>
            <CircularProgress aria-label="Loading executive briefing" />
            <Typography sx={{ color: THEME.textSecondary, fontSize: 14 }}>Loading executive briefing...</Typography>
          </Stack>
        </Box>
      ) : !effectiveTenantId ? (
        <Box component="main" sx={{ maxWidth: 1600, mx: "auto", px: { xs: 0, md: 0.5 } }}>
          <Paper
            elevation={0}
            sx={{
              ...surface,
              minHeight: 360,
              display: "grid",
              placeItems: "center",
              p: { xs: 3, md: 5 },
              textAlign: "center",
              bgcolor: "#FBFDFF"
            }}
          >
            <Box sx={{ maxWidth: 520 }}>
              <Box
                aria-hidden="true"
                sx={{
                  mx: "auto",
                  width: 64,
                  height: 64,
                  display: "grid",
                  placeItems: "center",
                  borderRadius: "50%",
                  bgcolor: "#E8F3FC",
                  color: THEME.primary
                }}
              >
                <BusinessOutlined sx={{ fontSize: 32 }} />
              </Box>
              <Typography component="h1" sx={{ mt: 2, color: "#092442", fontSize: 22, fontWeight: 800 }}>
                Select a customer to view the executive briefing
              </Typography>
              <Typography sx={{ mt: 0.8, color: THEME.textSecondary, fontSize: 14, lineHeight: 1.6 }}>
                Choose an account from the Customer portfolio selector above to load its executive overview, product
                engagement, leadership priorities, and key contacts.
              </Typography>
              <Chip
                label="Use the Customer portfolio selector to get started"
                size="small"
                sx={{ mt: 2, height: 26, bgcolor: "#EEF6FC", color: THEME.primary, fontSize: 13, fontWeight: 700 }}
              />
            </Box>
          </Paper>
        </Box>
      ) : (
        <Box key={effectiveTenantId} sx={{ px: { xs: 0, md: 0.5 } }}>
          <Box>
            <Typography sx={{ fontSize: 25, fontWeight: 780 }}>{customerName} Executive Briefing</Typography>
            <Box
              sx={{
                mt: 0.35,
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                rowGap: 0.6
              }}
            >
              {[
                {
                  label: "SEGMENT",
                  value: segment || "Not available",
                  icon: <GroupsOutlined sx={{ fontSize: 18 }} />,
                  background: "#E5F3FC",
                  color: "#0078D4"
                },
                {
                  label: "INDUSTRY",
                  value: industry || "Not available",
                  icon: <BusinessOutlined sx={{ fontSize: 18 }} />,
                  background: "#DFF7EF",
                  color: "#008C5A"
                },
                {
                  label: "REGION",
                  value: region || "Not available",
                  icon: <PublicOutlined sx={{ fontSize: 18 }} />,
                  background: "#EEE9FF",
                  color: "#5B36D6"
                }
              ].map(({ label, value, icon, background, color }, index) => (
                <Box
                  key={label}
                  sx={{
                    minWidth: 0,
                    px: index === 0 ? 0 : { xs: 1, sm: 1.5 },
                    pr: { xs: 1, sm: 1.5 },
                    py: 0.25,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.8,
                    borderLeft: index === 0 ? "none" : `1px solid ${THEME.border}`
                  }}
                >
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      flex: "0 0 auto",
                      display: "grid",
                      placeItems: "center",
                      borderRadius: "50%",
                      bgcolor: background,
                      color
                    }}
                  >
                    {icon}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 12, lineHeight: 1.2, color: THEME.textSecondary }}>{label}</Typography>
                    <Typography
                      title={value}
                      sx={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: "#092442",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap"
                      }}
                    >
                      {value}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
          {isError && (
            <Typography sx={{ mt: 0.8, fontSize: 14, color: THEME.error }}>
              The live API is unavailable in this environment. Function-backed fields will replace preview values
              automatically when connectivity is restored.
            </Typography>
          )}

          {/* <Box
          sx={{
            mt: 1.5,
            p: 1.2,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1,
            bgcolor: "#F7FAFC",
            border: `1px solid ${THEME.border}`,
            borderRadius: "5px"
          }}
        >
          <Typography sx={{ mr: 0.5, fontSize: 10, fontWeight: 800 }}>How to read this page:</Typography>
          <LiveTag />
          <Typography sx={{ fontSize: 10, color: THEME.textSecondary }}>
            returned by the Executive Briefing API
          </Typography>
          <PreviewTag />
          <Typography sx={{ fontSize: 10, color: THEME.textSecondary }}>
            placeholder content not returned by the API
          </Typography>
          <MixedTag />
          <Typography sx={{ fontSize: 10, color: THEME.textSecondary }}>card contains both sources</Typography>
        </Box> */}

          <Paper elevation={0} sx={{
            ...surface, mt: 1.5, p: 2, display: "grid",
            gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "minmax(0, 1fr) auto" },
            gridTemplateRows: "auto 1fr", columnGap: 2
          }}>
            <Box sx={{ display: "contents" }}>
              <Typography component="h2" sx={{ fontSize: 18, fontWeight: 750, color: "#092442" }}>
                Executive Overview
              </Typography>
              <Box sx={{
                textAlign: "right", alignSelf: "start",
                gridColumn: { xs: 1, sm: 2 }, gridRow: { xs: 3, sm: "1 / span 2" },
                mt: { xs: 1.5, sm: 0 }
              }}>
                <Typography sx={{ fontSize: 12, color: THEME.textSecondary }}>Was this summary helpful?</Typography>
                <Button
                  size="small"
                  variant={helpful === true ? "contained" : "outlined"}
                  startIcon={<ThumbUpOutlined />}
                  onClick={() => setHelpful(true)}
                  sx={{ mt: 0.4, mr: 0.5, textTransform: "none", fontSize: 13 }}
                >
                  Helpful
                </Button>
                <Button
                  size="small"
                  variant={helpful === false ? "contained" : "outlined"}
                  startIcon={<ThumbDownOutlined />}
                  onClick={() => setHelpful(false)}
                  sx={{ mt: 0.4, textTransform: "none", fontSize: 13 }}
                >
                  Not helpful
                </Button>
              </Box>
            </Box>
            <Box sx={{ mt: 0.5, minWidth: 0, gridColumn: 1, gridRow: 2 }}>
              <Box>
                {executiveOverviewQuery.isLoading || executiveOverviewQuery.isFetching ? (
                  <Stack direction="row" spacing={1} sx={{ mt: 0.7, alignItems: "center" }} role="status">
                    <CircularProgress size={18} aria-label="Loading executive overview" />
                    <Typography sx={{ fontSize: 14, color: THEME.textSecondary }}>
                      Loading executive overview...
                    </Typography>
                  </Stack>
                ) : executiveOverviewQuery.isError ? (
                  <Box role="alert" sx={{ mt: 0.7 }}>
                    <Typography sx={{ fontSize: 14, color: THEME.error }}>
                      The executive overview could not be loaded.
                    </Typography>
                    <Button size="small" onClick={() => executiveOverviewQuery.refetch()}>
                      Retry executive overview
                    </Button>
                  </Box>
                ) : currentPostureAISummary ? (
                  <Box
                    sx={{
                      mt: 0,
                      overflowY: "auto",
                      pr: 1,
                      color: "#29435E"
                    }}
                  >
                    {currentPostureContent.overview.map((paragraph, index) => {
                      const labeledParagraph = paragraph.match(/^([^:]+):\s*(.+)$/);
                      return (
                        <Typography
                          key={`current-posture-overview-${index + 1}`}
                          sx={{
                            mb: 1,
                            fontSize: 14,
                            fontWeight: 400,
                            lineHeight: 1.55,
                            color: "#49627B"
                          }}
                        >
                          {labeledParagraph ? (
                            <>
                              <Box component="span" sx={{ fontWeight: 800 }}>
                                {labeledParagraph[1]}:
                              </Box>{" "}
                              {labeledParagraph[2]}
                            </>
                          ) : (
                            paragraph
                          )}
                        </Typography>
                      );
                    })}
                    {currentPostureContent.sections.map((section, sectionIndex) => (
                      <Box
                        key={`current-posture-section-${sectionIndex + 1}`}
                        sx={{ mt: sectionIndex === 0 ? 1 : 1.4 }}
                      >
                        {section.heading && (
                          <Typography component="h4" sx={{ m: 0, fontSize: 14, fontWeight: 800, color: "#092442" }}>
                            {section.heading}
                          </Typography>
                        )}
                        <Box component="ul" sx={{ mt: section.heading ? 0.55 : 0, mb: 0, pl: 2.5 }}>
                          {section.bullets.map((bullet, bulletIndex) => (
                            <Typography
                              component="li"
                              key={`current-posture-section-${sectionIndex + 1}-bullet-${bulletIndex + 1}`}
                              sx={{ mb: 0.65, pl: 0.35, fontSize: 14, lineHeight: 1.55, color: "#29435E" }}
                            >
                              {bullet}
                            </Typography>
                          ))}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography sx={{ mt: 0.7, fontSize: 15, lineHeight: 1.55, color: "#29435E" }}>
                    No executive overview is available for the selected customer, product, and timeframe.
                  </Typography>
                )}
                {/* <Typography sx={{ mt: 1.6, fontSize: 11, fontWeight: 800 }}>Strategic context</Typography>
                            <Typography sx={{ mt: 0.7, fontSize: 12, lineHeight: 1.55, color: "#29435E" }}>The customer is entering an important E7 evaluation window as it develops its enterprise AI and security-governance strategy. This creates an opportunity to connect the integrated Microsoft security roadmap with renewal and expansion planning.</Typography> */}
              </Box>
              {/* <Box sx={{ p: 2, border: `1px solid ${THEME.border}`, borderRadius: "7px", bgcolor: "#F7FAFC" }}>
                            <Typography sx={{ fontSize: 9, fontWeight: 800, color: THEME.textSecondary }}>OVERALL CUSTOMER HEALTH</Typography>
                            <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1.5 }}><Box sx={{ width: 64, height: 64, borderRadius: "50%", border: "8px solid #107C10", display: "grid", placeItems: "center", fontSize: 18, fontWeight: 800 }}>{health.value}</Box><Box><Typography sx={{ fontSize: 14, fontWeight: 800 }}>Healthy {health.live ? <LiveTag /> : null}</Typography><Typography sx={{ fontSize: 10, fontWeight: 700, color: "#107C10" }}>↑ 6 points since July</Typography></Box></Box>
                            <Divider sx={{ my: 1.5 }} />
                            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>{[["Account tier", segment.value], ["Annual value represented", annualValue.value], ["Executive sponsor", "Jordan Mitchell"], ["Brief generated", generatedAt?.toLocaleString() || "Sep 15, 2026 · 3:30 PM"]].map(([label, value]) => <Box key={label}><Typography sx={{ fontSize: 9, color: THEME.textSecondary }}>{label}</Typography><Typography sx={{ mt: 0.2, fontSize: 10, fontWeight: 750 }}>{value}</Typography></Box>)}</Box>
                            {data && <Box sx={{ mt: 1.5 }}><LiveTag /><Typography sx={{ mt: 0.5, fontSize: 9 }}>{apertureResponses} survey responses · {apertureAnswers} answers</Typography></Box>}
                        </Box> */}
            </Box>
          </Paper>

          <Paper elevation={0} sx={{ ...surface, mt: 2.5, p: { xs: 2, sm: 3 }, borderRadius: 1.5 }}>
            <ProductSnapshots
              key={`${effectiveTenantId}|${selectedProduct}|${timeframe}`}
              tenantId={effectiveTenantId}
              feedbackPeriod={timeframe}
              data={productSnapshotQuery.currentData}
              timeframeLabel={formatTimeframeLabel(timeframe)}
              isFetching={productSnapshotQuery.isLoading || productSnapshotQuery.isFetching}
              isError={productSnapshotQuery.isError}
              onRetry={() => productSnapshotQuery.refetch()}
            />
          </Paper>

          {/* <Paper elevation={0} sx={{ ...surface, mt: 1.5, p: 2 }}>
                    <SectionHeading title="Account and commercial context" subtitle="Microsoft footprint, licensing, active opportunities, renewal timing, and competitive technologies" tag="preview" />
                    <Box sx={{ mt: 1.5, display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" }, gap: 1.2 }}>{[["MICROSOFT FOOTPRINT", "Microsoft 365 E5 + Security", "4 pilot products plus established GA security products"], ["ACTIVE OPPORTUNITY", opportunity.value, "Stage 3 · Decision expected Nov 2026"], ["RENEWAL", "142 days", "February 5, 2027 · Preparation active"], ["COMPETE", "CrowdStrike · Palo Alto · Splunk", "Endpoint, network security, and SOC analytics"]].map(([label, value, detail]) => <Box key={label} sx={{ p: 1.4, border: `1px solid ${THEME.border}`, borderRadius: "5px", bgcolor: "#FAFBFC" }}><Typography sx={{ fontSize: 9, fontWeight: 800, color: THEME.textSecondary }}>{label}</Typography><Typography sx={{ mt: 0.5, fontSize: 13, fontWeight: 800 }}>{value}</Typography><Typography sx={{ mt: 0.4, fontSize: 9, color: THEME.textSecondary }}>{detail}</Typography></Box>)}</Box>
                </Paper> */}

          <Box sx={{ mt: 1.5, display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 1.5 }}>
            <Paper elevation={0} sx={{ ...surface, p: { xs: 1.25, sm: 2 } }}>
              <SectionHeading
                title="Key contacts"
                subtitle="Primary stakeholders and their latest engagement context"
                tag="live"
              />
              {(isLoadingContacts || isFetchingContacts) && (
                <Box sx={{ minHeight: 160, display: "grid", placeItems: "center" }}>
                  <CircularProgress size={28} aria-label="Loading key contacts" />
                </Box>
              )}
              {!isLoadingContacts && !isFetchingContacts && isErrorContacts && (
                <Box sx={{ py: 3, textAlign: "center" }}>
                  <Typography sx={{ fontSize: 14, color: THEME.textSecondary }}>
                    Unable to load key contacts.
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={() => refetchContacts()}
                    sx={{ mt: 1, textTransform: "none" }}
                  >
                    Retry
                  </Button>
                </Box>
              )}
              {!isLoadingContacts && !isFetchingContacts && !isErrorContacts && contactGroups.length > 0 && (
                <Box
                  sx={{
                    mt: 1.5,
                    columnCount: { xs: 1, md: 2, xl: 3 },
                    columnGap: 1.2
                  }}
                >
                  {contactGroups.map((group, groupIndex) => {
                    const visibleContacts = group.contacts.slice(0, 3);
                    return (
                      <Box
                        key={`contactGroupV1-${group.label}-${groupIndex + 1}`}
                        sx={{
                          mb: 1.2,
                          width: "100%",
                          minWidth: 0,
                          display: "inline-block",
                          breakInside: "avoid",
                          overflow: "hidden",
                          border: `1px solid ${THEME.border}`,
                          borderRadius: "7px"
                        }}
                      >
                        <Box
                          sx={{
                            minHeight: 28,
                            px: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                            bgcolor: "#F1F4F7"
                          }}
                        >
                          <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#17395C" }}>
                            {group.label}
                          </Typography>
                          {group.contacts.length > visibleContacts.length && (
                            <Typography sx={{ fontSize: 12, color: "#17395C" }}>
                              {visibleContacts.length} shown of {group.contacts.length}
                            </Typography>
                          )}
                        </Box>
                        {visibleContacts.map((contact, contactIndex) => (
                          <ContactRow
                            key={
                              contact.id
                                ? `visibleContacts--${contact.id}-${contactIndex + 1}`
                                : `visibleContacts-${getContactName(contact)}-${contactIndex}`
                            }
                            contact={contact}
                            colorIndex={groupIndex + contactIndex}
                          />
                        ))}
                      </Box>
                    );
                  })}
                </Box>
              )}
              {!isLoadingContacts &&
                !isFetchingContacts &&
                !isErrorContacts &&
                (consolidatedTenantContacts?.length ?? 0) === 0 && (
                  <Box
                    sx={{
                      py: 3,
                      textAlign: "center",
                      fontSize: 14,
                      color: THEME.textSecondary
                    }}
                  >
                    No key contacts are available for this customer.
                  </Box>
                )}
              {!isLoadingContacts &&
                !isFetchingContacts &&
                !isErrorContacts &&
                (consolidatedTenantContacts?.length ?? 0) > 0 && (
                  <ButtonBase
                    onClick={() => setContactsDialogOpen(true)}
                    sx={{
                      mt: 1.5,
                      width: "100%",
                      minHeight: 34,
                      borderRadius: "7px",
                      bgcolor: "#F1F5FA",
                      color: "#075ED1",
                      fontSize: 13,
                      fontWeight: 800,
                      "&:hover": { bgcolor: "#E7EEF7" }
                    }}
                  >
                    View all {consolidatedTenantContacts?.length ?? 0} contacts
                    <ChevronRight sx={{ ml: 0.8, fontSize: 17 }} />
                  </ButtonBase>
                )}
            </Paper>
            <Dialog
              open={contactsDialogOpen}
              onClose={() => setContactsDialogOpen(false)}
              fullWidth
              maxWidth="sm"
              PaperProps={{
                sx: {
                  width: { xs: "calc(100% - 24px)", sm: 610 },
                  maxHeight: "calc(100vh - 32px)",
                  m: { xs: 1.5, sm: 2 },
                  borderRadius: "7px",
                  border: `1px solid ${THEME.border}`,
                  boxShadow: "0 12px 40px rgba(23, 43, 77, 0.18)"
                }
              }}
            >
              <DialogContent sx={{ p: 2, "&:first-of-type": { pt: 2 } }}>
                <Box sx={{ display: "flex", alignItems: "start", justifyContent: "space-between", gap: 2 }}>
                  <SectionHeading
                    title="All contacts"
                    subtitle="Primary stakeholders and their latest engagement context"
                    tag="live"
                  />
                  <IconButton
                    aria-label="Close all contacts"
                    onClick={() => setContactsDialogOpen(false)}
                    size="small"
                    sx={{ mt: -0.6, mr: -0.8, color: THEME.textSecondary }}
                  >
                    <Close sx={{ fontSize: 17 }} />
                  </IconButton>
                </Box>
                <Box
                  sx={{
                    mt: 1.5,
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr) 135px" },
                    gap: 1
                  }}
                >
                  <TextField
                    value={contactSearch}
                    onChange={(event) => setContactSearch(event.target.value)}
                    placeholder="Search by name, role, or email..."
                    size="small"
                    inputProps={{ "aria-label": "Search contacts" }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search sx={{ fontSize: 16, color: "#49627B" }} />
                        </InputAdornment>
                      )
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": { height: 32, fontSize: 13 },
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: THEME.border }
                    }}
                  />
                  <Select
                    value={contactRoleFilter}
                    onChange={(event) => setContactRoleFilter(event.target.value)}
                    size="small"
                    inputProps={{ "aria-label": "Filter contacts by role" }}
                    sx={{ height: 32, fontSize: 13, fontWeight: 700 }}
                  >
                    <MenuItem value="All roles" sx={{ fontSize: 13 }}>
                      All roles
                    </MenuItem>
                    {contactRoleOptions.map((role, index) => (
                      <MenuItem key={`contactRoleOption-${role}-${index + 1}`} value={role} sx={{ fontSize: 13 }}>
                        {role}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
                <Box sx={{ mt: 1.1, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#17395C" }}>
                    {filteredContactCount} {filteredContactCount === 1 ? "contact" : "contacts"}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                    <Typography sx={{ fontSize: 12, color: THEME.textSecondary }}>Sort by</Typography>
                    <Select
                      value={contactSort}
                      onChange={(event) => setContactSort(event.target.value as "asc" | "desc")}
                      size="small"
                      inputProps={{ "aria-label": "Sort contacts" }}
                      sx={{ height: 32, minWidth: 120, fontSize: 12 }}
                    >
                      <MenuItem value="asc" sx={{ fontSize: 13 }}>
                        Name (A-Z)
                      </MenuItem>
                      <MenuItem value="desc" sx={{ fontSize: 13 }}>
                        Name (Z-A)
                      </MenuItem>
                    </Select>
                  </Box>
                </Box>
                <Box sx={{ mt: 1, maxHeight: "min(560px, calc(100vh - 245px))", overflowY: "auto", pr: 0.3 }}>
                  {filteredContactGroups.map((group, groupIndex) => (
                    <Box
                      key={`contactGroup-${group.label}-${groupIndex + 1}`}
                      sx={{
                        mb: 1.2,
                        overflow: "hidden",
                        border: `1px solid ${THEME.border}`,
                        borderRadius: "7px"
                      }}
                    >
                      <Box
                        sx={{
                          minHeight: 28,
                          px: 1,
                          display: "flex",
                          alignItems: "center",
                          bgcolor: "#F1F4F7"
                        }}
                      >
                        <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#17395C" }}>
                          {group.label} ({group.contacts.length})
                        </Typography>
                      </Box>
                      {group.contacts.map((contact, contactIndex) => (
                        <ContactRow
                          key={
                            contact.id
                              ? `contactGroupV2--${contact.id}-${contactIndex + 1}`
                              : `contactGroupV2-${getContactName(contact)}-${contactIndex}`
                          }
                          contact={contact}
                          colorIndex={groupIndex + contactIndex}
                          showActions
                        />
                      ))}
                    </Box>
                  ))}
                  {filteredContactCount === 0 && (
                    <Box sx={{ py: 5, textAlign: "center" }}>
                      <Typography sx={{ fontSize: 14, color: THEME.textSecondary }}>
                        No contacts match the selected filters.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </DialogContent>
            </Dialog>
            {/* <Paper elevation={0} sx={{ ...surface, p: 2 }}>
                        <SectionHeading title="Recent engagement timeline" subtitle="A chronological view of customer touchpoints and decisions" tag="preview" />
                        <Box sx={{ mt: 1.8, position: "relative", maxWidth: 760, mx: "auto", px: { xs: 0, sm: 1 } }}>
                            <Box sx={{ position: "absolute", top: 22, bottom: 22, left: { xs: 61, sm: 79 }, width: 3, borderRadius: "3px", bgcolor: "#C9DCEB" }} />
                            {[
                                ["SEP", "09", "LATEST", "MDASH", "Production validation", "SOC leadership confirmed stable production usage and agreed on final success criteria.", "#107C10", "#E7F5E6"],
                                ["SEP", "05", "PRODUCT REVIEW", "Perception", "Private-preview review", "Customer reported strong detection quality and requested workflow integration with MDASH.", "#0078D4", "#E5F3FF"],
                                ["AUG", "28", "WORKSHOP", "Aperture", "Architecture workshop", "Data residency and delegated administration remained unresolved after the design review.", "#8764B8", "#F1ECF8"],
                                ["AUG", "21", "EXECUTIVE", "Account", "Executive account review", "CISO emphasized measurable risk reduction and one unified Microsoft security narrative.", "#CA5010", "#FCEFE7"]
                            ].map(([month, day, type, product, title, detail, accent, tint], index) => (
                                <Box key={`${month}-${day}`} sx={{ position: "relative", display: "grid", gridTemplateColumns: { xs: "48px 28px minmax(0, 1fr)", sm: "62px 34px minmax(0, 1fr)" }, gap: { xs: 0.8, sm: 1.2 }, pb: index === 3 ? 0 : 1.4 }}>
                                    <Box sx={{ alignSelf: "start", py: 0.6, textAlign: "center", bgcolor: "#F7FAFC", border: `1px solid ${THEME.border}`, borderRadius: "5px" }}>
                                        <Typography sx={{ fontSize: 8, lineHeight: 1, fontWeight: 800, color: THEME.textSecondary }}>{month}</Typography>
                                        <Typography sx={{ mt: 0.3, fontSize: 17, lineHeight: 1, fontWeight: 800, color: "#092442" }}>{day}</Typography>
                                    </Box>
                                    <Box sx={{ zIndex: 1, display: "flex", justifyContent: "center", pt: 0.7 }}>
                                        <Box sx={{ width: 23, height: 23, display: "grid", placeItems: "center", borderRadius: "50%", bgcolor: accent, color: "#FFF", border: "3px solid #FFF", boxShadow: `0 0 0 2px ${accent}`, fontSize: 9, fontWeight: 800 }}>
                                            {index + 1}
                                        </Box>
                                    </Box>
                                    <Box sx={{ position: "relative", p: 1.2, bgcolor: "#FFF", border: `1px solid ${THEME.border}`, borderLeft: `4px solid ${accent}`, borderRadius: "6px", boxShadow: "0 2px 7px rgba(9, 36, 66, 0.07)", "&::before": { content: "\"\"", position: "absolute", top: 13, left: -8, width: 8, height: 8, bgcolor: "#FFF", borderLeft: `1px solid ${THEME.border}`, borderBottom: `1px solid ${THEME.border}`, transform: "rotate(45deg)" } }}>
                                        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 0.6 }}>
                                            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.6 }}>
                                                <Chip label={type} size="small" sx={{ height: 18, borderRadius: "3px", bgcolor: tint, color: accent, fontSize: 7, fontWeight: 800 }} />
                                                <Typography sx={{ fontSize: 10, fontWeight: 800, color: accent }}>{product}</Typography>
                                            </Box>
                                            {index === 0 && <Chip icon={<CheckCircle />} label="Completed" size="small" sx={{ height: 19, bgcolor: "#E7F5E6", color: "#107C10", fontSize: 8, fontWeight: 750, "& .MuiChip-icon": { ml: 0.5, fontSize: 13, color: "#107C10" } }} />}
                                        </Box>
                                        <Typography sx={{ mt: 0.6, fontSize: 12, fontWeight: 800, color: "#092442" }}>{title}</Typography>
                                        <Typography sx={{ mt: 0.35, fontSize: 9, lineHeight: 1.45, color: THEME.textSecondary }}>{detail}</Typography>
                                        <Box sx={{ mt: 0.7, display: "flex", flexWrap: "wrap", gap: 1.2 }}>
                                            <Typography sx={{ fontSize: 8, fontWeight: 800, color: "#145A8D" }}>ENGAGEMENT NOTES</Typography>
                                            <Typography sx={{ fontSize: 8, fontWeight: 800, color: "#145A8D" }}>FEATURE REQUESTS</Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Paper> */}
          </Box>
        </Box>
      )}

    </Box>
  );
};

export default ExecutiveBriefing;
